import { Box, Button, Typography } from '@mui/material'
import React from 'react'

import { useConfirmation } from '@/app/contexts/confirmation.context'
import { OrgPlan } from '@/app/module/plans/types'
import { useCancelSubscription } from '@/app/module/user/hooks/use-cancel-subscription'
import { useUpdateSubscription } from '@/app/module/user/hooks/use-update-subscription'
import { openClearFeed } from '@/app/service/clearfeed/utils'

import CancelSubscriptionConfirmation from './cancel-subscription-confirmation'

type Props = {
  activePlan: OrgPlan
}

/**
 *  Assuming the user has edaquate permissions, this component allows the user to change their subscription plan.
 *  There should be an active plan.
 */
const ChangeSubscription: React.FC<Props> = ({ activePlan }) => {
  const { canceled } = activePlan
  const createConfirmation = useConfirmation()
  const [cancelSubscription, { isLoading: isCanceling }] = useCancelSubscription()
  const [updateSubscription, { isLoading: isReactivating }] = useUpdateSubscription()
  const [isCancelDialogOpen, setIsCancelDialogOpen] = React.useState(false)

  const openCancelDialog = () => {
    setIsCancelDialogOpen(true)
  }

  const closeCancelDialog = () => {
    setIsCancelDialogOpen(false)
  }

  const handleCancelSubscription = async () => {
    try {
      await cancelSubscription()
    } finally {
      closeCancelDialog()
    }
  }

  const handleReactivateSubscription = React.useCallback(async () => {
    if (isReactivating) {
      return
    }

    const confirmed = await createConfirmation({
      confirmButtonLabel: 'Reactivate',
      content: (
        <Typography>
          Are you sure you want to reactivate your subscription? Your subscription will automatically renew at the end
          of the current billing period.
        </Typography>
      ),
      title: 'Reactivate Subscription',
    })

    if (!confirmed) {
      return
    }

    await updateSubscription({
      billing: activePlan.billing,
      planId: activePlan.planId,
      undoCancel: true,
      undoDowngrade: false,
    })
  }, [activePlan.billing, activePlan.planId, createConfirmation, isReactivating, updateSubscription])

  if (canceled) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2">
          To change your plan, please first cancel the expiration of the current plan.
        </Typography>

        <Typography variant="body2">
          Undo the cancelation and automatically renew my subscription at the end of the current billing period:
        </Typography>

        <div>
          <Button
            variant="contained"
            color="primary"
            disabled={isReactivating}
            loading={isReactivating}
            onClick={handleReactivateSubscription}
          >
            Reactivate my subscription
          </Button>
        </div>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2">To change your plan, please</Typography>
        <Button color="primary" size="small" variant="contained" onClick={() => openClearFeed()}>
          Contact support
        </Button>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2">Cancel your subscription to the end of the current billing period:</Typography>

        <Button color="error" variant="outlined" sx={{ mt: 1 }} onClick={openCancelDialog} loading={isCanceling}>
          Cancel subscription
        </Button>
      </Box>

      <CancelSubscriptionConfirmation
        activePlan={activePlan}
        open={isCancelDialogOpen}
        onClose={closeCancelDialog}
        onCancel={handleCancelSubscription}
        isLoading={isCanceling}
      />
    </Box>
  )
}

export default ChangeSubscription
